import React from "react";
import { connect } from "react-redux";
import { Col, Dropdown, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import Select from "react-select";

import CsLineIcons from "../../../cs-line-icons";
import Table from "../../../layout/table";
import { initialState } from "../../../layout/table/helpers";

import { search as searchCheckRegister, markPrinted } from "../../../actions/check.register";
import { get as getFinancialAccounts } from "../../../actions/financial.accounts";
import { get as getSetting } from "../../../actions/settings";
import { render as renderReport } from "../../../actions/report.template";
import { reverse as reverseJournalEntry } from "../../../actions/journal.entries";
import { pack as sendAlert } from "../../../actions/alert";
import { send as pageInfo, loading } from "../../../actions/page";
import { downloadCheckPdf } from "../../../utils/checks";
import { format as formatDate } from "../../../utils/date";
import { currency as formatCurrency } from "../../../utils/number";
import { isGuid } from "../../../utils/string";
import { EDIT_CHECK } from "../../../constants/permissions";

const parseSetting = (setting) => {
  const value = setting?.value ?? setting;
  if (!value) return {};
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch (_err) {
    return {};
  }
};

const financialAccountLabel = (account) => {
  const institution = String(account?.financialInstitution || "").trim();
  const name = String(account?.name || "").trim();
  const lastFour = String(account?.lastFour || "").trim();
  const suffix = lastFour ? ` ...${lastFour}` : "";
  return [name, institution].filter(Boolean).join(" - ") + suffix;
};

class CheckRegister extends React.Component {
  sizeOptions = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
  ];

  selectStyles = {
    container: (base) => ({ ...base, minWidth: 86 }),
    control: (base) => ({
      ...base,
      minHeight: 26,
      height: 26,
      borderColor: "transparent",
      boxShadow: "none",
      backgroundColor: "transparent",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 4px", height: 26 }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 26 }),
    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
    indicatorSeparator: () => ({ display: "none" }),
    singleValue: (base) => ({ ...base, fontSize: 12, color: "#6c757d" }),
    menu: (base) => ({ ...base, zIndex: 20 }),
  };

  constructor(props) {
    super(props);

    this.state = {
      ...initialState({ order: "checkDate", direction: "DESC", size: props.embedded ? 10 : 25 }),
      count: 0,
      financialAccounts: [],
      financialAccountUuid: props.financialAccountUuid || "",
      accountsSetting: {},
    };
  }

  componentDidMount = async () => {
    if (!this.props.embedded) await this.props.pageInfo({ title: "Check Register", breadcrumb: "Check Register" });
    await this.loadOptionsAndData();
  };

  componentDidUpdate = async (oldProps) => {
    if (oldProps.financialAccountUuid !== this.props.financialAccountUuid) {
      this.setState({ financialAccountUuid: this.props.financialAccountUuid || "", page: 0 }, this.loadData);
    }

    if (oldProps.updated !== this.props.updated) await this.loadData();
  };

  get effectiveFinancialAccountUuid() {
    return this.props.financialAccountUuid || this.state.financialAccountUuid || "";
  }

  loadOptionsAndData = async () => {
    this.props.loading(true);

    try {
      const [financialAccounts, accountsSetting] = await Promise.all([
        this.props.embedded ? Promise.resolve([]) : this.props.getFinancialAccounts(null),
        this.props.getSetting({ name: "ACCOUNTS" }),
      ]);

      this.setState({
        financialAccounts: financialAccounts || [],
        accountsSetting: parseSetting(accountsSetting),
      }, this.loadData);
    } catch (_err) {
      this.props.loading(false);
    }
  };

  loadData = async () => {
    const { page, size, order, direction, search } = this.state;
    const result = await this.props.searchCheckRegister({
      page,
      size,
      order,
      direction,
      search,
      financialAccountUuid: this.effectiveFinancialAccountUuid,
    });

    const count = Number(result?.count || 0);
    this.setState({
      data: result?.items || [],
      count,
      pages: size > 0 ? Math.ceil(count / Number(size)) : 0,
    });

    this.props.loading(false);
  };

  get financialAccountOptions() {
    return (this.state.financialAccounts || [])
      .filter((account) => account?.uuid && account.isActive !== false)
      .map((account) => ({
        value: account.uuid,
        label: financialAccountLabel(account),
      }));
  }

  get selectedFinancialAccountOption() {
    return this.financialAccountOptions.find((option) => option.value === this.effectiveFinancialAccountUuid) || null;
  }

  get sizeValue() {
    return this.sizeOptions.find((option) => option.value === Number(this.state.size)) || this.sizeOptions[0];
  }

  get headers() {
    const headers = [
      {
        header: "Date",
        sortable: true,
        source: "checkDate",
        className: "text-muted text-small text-uppercase w-10",
        onRender: (value, row) => this.renderValue(formatDate(value), row),
      },
      {
        header: "Check",
        sortable: true,
        source: "checkNumber",
        className: "text-muted text-small text-uppercase w-10",
        onRender: (value, row) => this.renderValue(value || row.referenceNumber || "", row),
      },
      {
        header: "Payee",
        sortable: true,
        source: "payeeName",
        className: "text-muted text-small text-uppercase w-20",
        onRender: (value, row) => this.renderValue(value || "", row),
      },
      {
        header: "Memo",
        sortable: false,
        source: "memo",
        className: "text-muted text-small text-uppercase w-25",
        onRender: (value, row) => this.renderValue(value || row.sourceType || "", row),
      },
      {
        header: "Account",
        sortable: false,
        source: "financialAccount.name",
        className: "text-muted text-small text-uppercase w-15",
        onRender: (_value, row) => this.renderFinancialAccount(row),
      },
      {
        header: "Status",
        sortable: false,
        source: "checkStatus",
        className: "text-muted text-small text-uppercase w-10",
        onRender: (_value, row) => this.renderStatus(row),
      },
      {
        header: "Amount",
        sortable: true,
        source: "amount",
        className: "text-muted text-small text-uppercase w-10 text-end",
        dataClassName: "text-end",
        onRender: (value, row) => this.renderValue(formatCurrency(Number(value || 0)), row),
      },
      {
        header: "",
        source: "uuid",
        className: "text-muted text-small text-uppercase w-10 text-end",
        dataClassName: "text-end",
        onRender: (_value, row) => this.renderActions(row),
      },
    ];

    return this.props.embedded
      ? headers.filter((header) => header.header !== "Account")
      : headers;
  }

  isVoided = (row) => row?.status === "VOIDED";

  renderValue = (text, row) => (
    <span className={this.isVoided(row) ? "text-decoration-line-through text-muted" : undefined}>
      {text}
    </span>
  );

  renderFinancialAccount = (row) => {
    const account = row?.financialAccount;
    if (!account?.uuid) return this.renderValue("", row);

    return (
      <a
        href={`/dashboard/accounting/financial-accounts/${account.uuid}`}
        className={this.isVoided(row) ? "text-decoration-line-through text-muted" : undefined}
      >
        {financialAccountLabel(account)}
      </a>
    );
  };

  renderCleared = (row) => {
    if (!row?.clearedAt) return "";

    const label = `Cleared ${formatDate(row.clearedAt)}`;
    if (!isGuid(row.bankReconciliationUuid)) return this.renderValue(label, row);

    return (
      <a
        href={`/dashboard/accounting/reconciliations/${row.bankReconciliationUuid}`}
        className={this.isVoided(row) ? "text-decoration-line-through text-muted" : undefined}
      >
        {label}
      </a>
    );
  };

  renderStatus = (row) => {
    const status = row.checkStatus || (row.clearedAt ? "CLEARED" : "UNPRINTED");
    const badge = status === "VOIDED"
      ? "bg-danger"
      : status === "CLEARED"
        ? "bg-success"
        : status === "REPRINTED"
          ? "bg-warning text-dark"
          : status === "PRINTED"
          ? "bg-primary"
            : "bg-light text-dark";
    const baseLabel = status.charAt(0) + status.slice(1).toLowerCase();
    const date = status === "CLEARED"
      ? row.clearedAt
      : status === "REPRINTED"
        ? row.reprintedAt
        : status === "PRINTED"
          ? row.printedAt
          : row.voidedAt;
    const label = date ? `${baseLabel} ${formatDate(date)}` : baseLabel;
    const className = `badge ${badge}`;

    if (status === "CLEARED" && isGuid(row.bankReconciliationUuid)) {
      return (
        <a href={`/dashboard/accounting/reconciliations/${row.bankReconciliationUuid}`} className={className}>
          {label}
        </a>
      );
    }

    return (
      <span className={className}>{label}</span>
    );
  };

  renderActions = (row) => (
    <Dropdown align="end">
      <Dropdown.Toggle
        variant="outline-primary"
        className="btn-sm btn-icon btn-icon-only shadow-none"
        disabled={row.status !== "POSTED" || !this.canEditChecks}
        aria-label="Check actions"
      >
        <CsLineIcons icon="more-horizontal" />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item href="#" onClick={(e) => this.onPrintCheckClick(e, row)}>
          Print Check
        </Dropdown.Item>
        <Dropdown.Item href="#" className="text-danger" onClick={(e) => this.onReverseClick(e, row)}>
          Void
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  templateUuidFor = (row) => {
    if (row?.sourceType === "PAYROLL_CHECK") {
      return this.state.accountsSetting?.payroll?.check || this.state.accountsSetting?.general?.check;
    }

    return this.state.accountsSetting?.general?.check || this.state.accountsSetting?.payroll?.check;
  };

  onPrintCheck = async (row) => {
    if (!this.canEditChecks) return;

    try {
      await downloadCheckPdf({
        render: this.props.renderReport,
        templateUuid: this.templateUuidFor(row),
        check: {
          amount: row.amount,
          checkDate: row.checkDate,
          checkNumber: row.checkNumber,
          memo: row.memo,
          payeeName: row.payeeName,
          payeeAddress: row.payeeAddress,
          referenceNumber: row.referenceNumber,
        },
        fileName: `${formatDate(row.checkDate)} Check ${row.checkNumber || row.payeeName || row.uuid}.pdf`,
      });
      await this.props.markPrinted({
        journalEntryUuid: row.journalEntryUuid,
        financialAccountUuid: row.financialAccountUuid,
        checkNumber: row.checkNumber,
        checkDate: row.checkDate,
      });
      await this.loadData();
    } catch (_err) {
      this.props.sendAlert(["Unable to print check."], "danger");
    }
  };

  onPrintCheckClick = (e, row) => {
    e.preventDefault();
    this.onPrintCheck(row);
  };

  onReverse = async (row) => {
    if (!this.canEditChecks) return;
    if (!window.confirm("Void this check entry? A reversing journal entry will be created.")) return;

    this.props.loading(true);
    const result = await this.props.reverseJournalEntry(row.journalEntryUuid);
    if (result?.success !== false) this.props.sendAlert(["check voided"], "success");
    await this.loadData();
  };

  get canEditChecks() {
    return (this.props.auth?.permissions || []).includes(EDIT_CHECK);
  }

  onReverseClick = (e, row) => {
    e.preventDefault();
    this.onReverse(row);
  };

  onFinancialAccountChange = (option) => {
    if (this.props.embedded) return;

    this.setState({ financialAccountUuid: option?.value || "", page: 0 }, () => {
      this.props.loading(true);
      this.loadData();
    });
  };

  onSearch = (search) => {
    this.setState({ search, page: 0 }, this.loadData);
  };

  onPageChange = (page) => {
    this.setState({ page: Number(page) }, this.loadData);
  };

  onSize = (size) => {
    this.setState({ size: Number(size), page: 0 }, this.loadData);
  };

  onColumnClick = (header) => {
    if (!header?.sortable) return;
    const order = header.source;
    const direction = this.state.order === order && this.state.direction === "ASC" ? "DESC" : "ASC";
    this.setState({ order, direction, page: 0 }, this.loadData);
  };

  render() {
    if (this.props.embedded && !isGuid(this.effectiveFinancialAccountUuid)) return null;

    if (this.props.embedded) {
      return (
        <>
          <Col xs="12" className="d-flex align-items-center justify-content-between mt-5">
            <h2 className="small-title mb-0">Check Register ({this.state.count})</h2>

            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Rows</span>
              <Select
                classNamePrefix="ra"
                value={this.sizeValue}
                options={this.sizeOptions}
                onChange={(option) => this.onSize(option?.value)}
                isSearchable={false}
                styles={this.selectStyles}
                aria-label="Rows per page"
              />
            </div>
          </Col>

          <Col xs="12">
            <Table
              headers={this.headers}
              page={this.state.data}
              size={this.state.size}
              index={this.state.page}
              pages={this.state.pages}
              onColumnClick={this.onColumnClick}
              onPageChange={this.onPageChange}
              searchable={false}
              sizable={false}
            />
          </Col>
        </>
      );
    }

    return (
      <>
        <Col xs="12">
          <Row className="align-items-end g-3">
            <Col xs="12" lg="6">
              <div className="mb-3 top-label">
                <Select
                  inputId="checkRegisterFinancialAccountUuid"
                  classNamePrefix="react-select"
                  isClearable
                  placeholder="All financial accounts"
                  options={this.financialAccountOptions}
                  value={this.selectedFinancialAccountOption}
                  onChange={this.onFinancialAccountChange}
                />
                <label>FINANCIAL ACCOUNT</label>
              </div>
            </Col>
            <Col xs="12" lg="6" className="text-muted small text-lg-end mb-3">
              {this.effectiveFinancialAccountUuid ? "Filtered" : "All financial accounts"} - {this.state.count} checks
            </Col>
          </Row>
        </Col>

        <Col xs="12">
          <Table
            headers={this.headers}
            page={this.state.data}
            size={this.state.size}
            index={this.state.page}
            pages={this.state.pages}
            search={this.state.search}
            onSearch={this.onSearch}
            onColumnClick={this.onColumnClick}
            onPageChange={this.onPageChange}
            onSize={this.onSize}
            searchable
            sizable
            searchOnEnter
          />
        </Col>
      </>
    );
  }
}

CheckRegister.propTypes = {
  loading: PropTypes.func.isRequired,
  pageInfo: PropTypes.func.isRequired,
  searchCheckRegister: PropTypes.func.isRequired,
  markPrinted: PropTypes.func.isRequired,
  getFinancialAccounts: PropTypes.func.isRequired,
  getSetting: PropTypes.func.isRequired,
  renderReport: PropTypes.func.isRequired,
  reverseJournalEntry: PropTypes.func.isRequired,
  sendAlert: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
  financialAccountUuid: PropTypes.string,
  updated: PropTypes.number,
  auth: PropTypes.object,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  searchCheckRegister,
  markPrinted,
  getFinancialAccounts,
  getSetting,
  renderReport,
  reverseJournalEntry,
  sendAlert,
  pageInfo,
  loading,
})(CheckRegister);
